// QR Kiosk v2 — §8.4 fullscreen page/document artifact takeover. Dark surround for
// schematic contrast, library-free zoom (100/175/250% width + native-scroll pan,
// double-tap toggles 100↔250), +/- controls above the §7 back pill. The image is
// auth-fetched (view.url is pre-resolved by resolveKioskArtifactImageUrl → the
// /v1/bot/diagram/{page}/image endpoint for schema pages, desktop parity). back ≠
// destroy — the artifact stays resolvable so re-tapping the chip reopens it.
// Nested surface → §7 shared BackPill, label kioskBack "Späť". Tokens + glyphs only.
import React, { useEffect, useState } from "react";
import useTranslation from "../../../shared/hooks/useTranslation";
import chatBotApi from "../../../shared/chatBot/ChatBotApi";
import { Machine } from "../../models/machinesModels";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { BackPill } from "../shared/BackPill";
import { ErrorCallout } from "../shared/ErrorCallout";

// Local auth'd image loader with an EXPLICIT failure state — useBotImage returns
// null for both loading AND error, but §5c needs a real failure branch (retry
// callout). Same chatBotApi.fetchBotImage proxy as useBotImage; `reloadKey` bump
// re-triggers the fetch.
function useArtifactImage(url: string, reloadKey: number): { blob: string | null; failed: boolean } {
  const [blob, setBlob] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    let created: string | null = null;
    setBlob(null);
    setFailed(false);
    chatBotApi
      .fetchBotImage(url)
      .then((b) => {
        if (active) {
          created = b;
          setBlob(b);
        } else {
          URL.revokeObjectURL(b);
        }
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [url, reloadKey]);
  return { blob, failed };
}

const ZOOMS = [100, 175, 250];

const zoomBtn: React.CSSProperties = {
  width: 44,
  height: 44,
  borderRadius: 999,
  background: "var(--card)",
  border: "1px solid var(--border)",
  boxShadow: "var(--shadow)",
  color: "var(--ink-2)",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

export interface MidasArtifactViewV2Props {
  machine: Machine;
  view: { url: string; title: string };
  onBack: () => void;
}

export function MidasArtifactViewV2({ machine, view, onBack }: MidasArtifactViewV2Props) {
  const { translate } = useTranslation("public");
  const [widthPct, setWidthPct] = useState(100);
  const [reloadKey, setReloadKey] = useState(0);
  const { blob, failed } = useArtifactImage(view.url, reloadKey);
  const zoomInPress = usePressed();
  const zoomOutPress = usePressed();

  const canZoomIn = widthPct < ZOOMS[ZOOMS.length - 1];
  const canZoomOut = widthPct > ZOOMS[0];
  const zoomIn = () => setWidthPct((w) => ZOOMS[Math.min(ZOOMS.indexOf(w) + 1, ZOOMS.length - 1)] ?? w);
  const zoomOut = () => setWidthPct((w) => ZOOMS[Math.max(ZOOMS.indexOf(w) - 1, 0)] ?? w);
  const toggleZoom = () => setWidthPct((w) => (w >= 250 ? 100 : 250));

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      {/* Top bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          height: 54,
          padding: "0 14px",
          background: "var(--card)",
          borderBottom: "1px solid var(--border)",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: "var(--midas-soft)",
            color: "var(--midas)",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Glyph d={GD.doc} size={18} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: "var(--ink)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {view.title}
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              color: "var(--faint)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {machine.model}
          </div>
        </div>
      </div>

      {/* Image area — dark surround, native-scroll pan. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          background: "color-mix(in oklab, var(--ink) 92%, black)",
          position: "relative",
        }}
      >
        {failed ? (
          <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <ErrorCallout
              title={translate("kioskV2MidasArtifactLoadFailed")}
              onRetry={() => setReloadKey((k) => k + 1)}
              retryLabel={translate("kioskV2Retry")}
            />
          </div>
        ) : !blob ? (
          <div className="kv2-sk" style={{ width: "100%", height: "100%" }} />
        ) : (
          // Vertical centring that survives zoom. The wrapper is `min-height: 100%`, so
          // it fills the pane when the image is shorter (centred by justify-content on
          // the column's main axis) and GROWS past it once the image is taller — free
          // space is then 0, so nothing is pushed into unscrollable overflow.
          // Horizontal centring stays on the image's AUTO MARGINS: auto margins clamp to
          // zero when free space is negative, so a 175/250% image sits flush-left and
          // every pixel is reachable by panning. `justify-content: center` on the inline
          // axis would instead strand the left half outside the scrollable region.
          //
          // ZOOM ROOT CAUSE (fixed 2026-08-04) — `max-width: none` below is LOAD-BEARING.
          // index.scss line 1 does `@import "tailwindcss/base"`, so Tailwind Preflight's
          // UNSCOPED `img, video { max-width: 100%; height: auto }` applies to this image
          // (verified in the shipped bundle). `max-width` is a DIFFERENT property from
          // `width`, so an inline `width: 175%` does NOT win over it on specificity — CSS
          // clamps the USED width to 100% of the containing block. The image therefore
          // rendered pixel-identical at 100/175/250 and the +/- buttons (and the
          // double-tap toggle) looked completely inert even though the state machine was
          // stepping correctly. Do not "tidy" this line away.
          <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <img
              src={blob}
              alt={view.title}
              onClick={toggleZoom}
              style={{
                width: `${widthPct}%`,
                maxWidth: "none", // defeats Tailwind Preflight's img{max-width:100%} — see note above
                height: "auto",
                display: "block",
                margin: "0 auto",
                cursor: widthPct >= 250 ? "zoom-out" : "zoom-in",
              }}
            />
          </div>
        )}
      </div>

      {/* Zoom controls — above the back pill. */}
      {blob && !failed && (
        <div
          style={{
            position: "fixed",
            right: 14,
            bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)",
            zIndex: 43,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <button
            type="button"
            aria-label={translate("kioskV2MidasZoomIn")}
            disabled={!canZoomIn}
            {...zoomInPress.pressHandlers}
            onClick={zoomIn}
            style={{ ...zoomBtn, opacity: canZoomIn ? 1 : 0.4, ...zoomInPress.pressStyle }}
          >
            <Glyph d={GD.plus} size={18} />
          </button>
          <button
            type="button"
            aria-label={translate("kioskV2MidasZoomOut")}
            disabled={!canZoomOut}
            {...zoomOutPress.pressHandlers}
            onClick={zoomOut}
            style={{ ...zoomBtn, opacity: canZoomOut ? 1 : 0.4, ...zoomOutPress.pressStyle }}
          >
            <Glyph d={GD.minus} size={18} />
          </button>
        </div>
      )}

      <BackPill label={translate("kioskBack")} onBack={onBack} zIndex={42} />
    </div>
  );
}

export default MidasArtifactViewV2;
